import React from "react";
import { Link } from "react-router-dom";
import { ContactForm } from "../components/ContactForm";
import { FaqAccordion } from "../components/FaqAccordion";
import FooterSection from "../components/FooterSection";
import { useSeo } from "../lib/useSeo";

const FAQ_ITEMS = [
  {
    id: 1,
    question: "WHAT IS THE DEBT COLLECTOR KILL SWITCH?",
    answer:
      "The Debt Collector Kill Switch is a DIY digital educational and document-drafting product containing 25+ attack files, 68 account-level validation demands, bureau dispute files, CFPB complaint and escalation materials, response and rebuttal files, legal/emergency preparation materials, Read First instructions, and additional tools organized around different collection situations.",
  },
  {
    id: 2,
    question: "IS THIS A DONE-FOR-YOU SERVICE?",
    answer:
      "No. The Kill Switch is designed for you to use yourself. Choose your situation, open the matching file, follow the instructions, fill in the required information, and send or use the appropriate document yourself.",
  },
  {
    id: 3,
    question: "HOW DO I RECEIVE THE KILL SWITCH AFTER I PURCHASE?",
    answer:
      "The Kill Switch is a digital product. After a successful purchase, you receive immediate private access to the digital materials and delivery instructions associated with your order.",
  },
  {
    id: 4,
    question: "WHERE DO I START?",
    answer:
      "Start with the Choose Your Situation and Read First materials. They are designed to help you identify the files that match your situation and understand what to complete, where to send it, and what comes next.",
  },
  {
    id: 5,
    question: "WHAT IF THE DEBT COLLECTOR RESPONDS?",
    answer:
      "The Kill Switch includes response and escalation materials for several common situations, including weak validation responses, no response, verification issues, bureau disputes, CFPB escalation, and more advanced collection situations. Use the instructions that correspond to your actual situation.",
  },
  {
    id: 6,
    question: "DOES THE KILL SWITCH GUARANTEE A COLLECTION WILL BE REMOVED?",
    answer:
      "No. No legitimate dispute or validation product can guarantee a particular outcome. Results depend on the account, documentation, reporting, collector or furnisher response, timing, applicable law, and the facts of your individual situation.",
  },
  {
    id: 7,
    question: "WHAT IF I HAVE ALREADY BEEN SUED OR HAVE A JUDGMENT OR GARNISHMENT?",
    answer:
      "The Kill Switch includes materials addressing legal threats, lawsuits, judgments, garnishments, levies, and related situations. These matters can involve strict court deadlines and state-specific procedures. The materials are educational and document-drafting resources and are not legal representation. If you have an active lawsuit or deadline, consider consulting a licensed attorney promptly.",
  },
  {
    id: 8,
    question: "DO I NEED TO BE AN ATTORNEY TO USE THIS?",
    answer:
      "No. The materials are organized for consumers to follow themselves. However, the Kill Switch does not replace individualized legal advice, and you remain responsible for deciding how to use the materials in your situation.",
  },
  {
    id: 9,
    question: "DO YOU OFFER REFUNDS?",
    answer: (
      <>
        Because the Kill Switch is an immediately accessible digital product that can be downloaded, saved, copied, or
        used after delivery, purchases are final and non-refundable except where required by applicable law. If you
        genuinely feel the Product is not appropriate for your situation or are confused about where to begin, contact
        us and we will try to help you understand and use the materials you purchased. See our{" "}
        <Link to="/refund-policy" className="text-brand-red font-bold hover:underline">
          Refund Policy
        </Link>
        .
      </>
    ),
  },
  {
    id: 10,
    question: "IS THE DEBT COLLECTOR KILL SWITCH AVAILABLE OUTSIDE THE UNITED STATES?",
    answer:
      "No. The Debt Collector Kill Switch is offered only to U.S. residents located in the United States who are 18 years of age or older.",
  },
  {
    id: 11,
    question: "I DIDN\u2019T RECEIVE MY ORDER OR CANNOT ACCESS MY FILES. WHAT SHOULD I DO?",
    answer:
      "Use the contact form above and select Order / Access Issue. Include the email address used for your order and your Order ID if you have it so we can help locate the purchase.",
  },
  {
    id: 12,
    question: "HOW DO I CONTACT SUPPORT?",
    answer: (
      <>
        Use the contact form above or email <span className="font-bold">info@debtcollectorkillswitch.com</span>.
        <br />
        Hours of Operation: <span className="font-bold">9:00 AM &ndash; 5:00 PM Pacific Time</span>.
        <br />
        Please allow approximately <span className="font-bold">24&ndash;72 hours for a response.</span>
      </>
    ),
  },
];

export default function ContactPage() {
  useSeo({
    title: "Contact Us & FAQ | Debt Collector Kill Switch",
    description:
      "Contact Debt Collector Kill Switch support for product, order, access or billing questions and find answers to frequently asked questions.",
    canonical: "https://debtcollectorkillswitch.com/contact",
  });

  return (
    <div className="min-h-screen bg-brand-ivory text-brand-dark font-sans" data-testid="contact-page">
      {/* Compact Header */}
      <header className="bg-brand-ivory border-b border-brand-border py-4 px-4 md:px-6" data-testid="contact-page-header">
        <div className="max-w-[1000px] mx-auto flex items-center justify-between">
          <span className="font-display font-black uppercase text-sm md:text-base text-brand-dark tracking-wide">
            DEBT COLLECTOR KILL SWITCH
          </span>
          <Link
            to="/"
            data-testid="contact-back-to-home-link"
            className="font-sans font-bold text-xs md:text-sm text-brand-red hover:text-brand-cta-hover transition-colors whitespace-nowrap"
          >
            &larr; BACK TO HOME
          </Link>
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* Page Title */}
        <div className="text-center mb-8">
          <h1
            className="font-display font-black uppercase text-brand-dark tracking-tight text-3xl sm:text-4xl mb-3"
            data-testid="contact-page-title"
          >
            CONTACT US
          </h1>
          <p className="font-sans font-bold uppercase text-brand-dark text-sm sm:text-base mb-4 max-w-lg mx-auto">
            HAVE QUESTIONS OR NEED ASSISTANCE?
          </p>
          <h2 className="font-display font-black uppercase text-brand-dark text-sm sm:text-base tracking-wide">
            HOURS OF OPERATION | MON - FRI | 9:00am &ndash; 5:00pm PST
          </h2>
          <p className="font-sans text-brand-dark/60 text-xs sm:text-sm mt-1" data-testid="contact-response-time">
            <span className="whitespace-nowrap">SEND US YOUR MESSAGE BELOW.</span>
            <br />
            WE WILL RESPOND AS SOON AS POSSIBLE.
          </p>
        </div>

        {/* Contact Form */}
        <ContactForm />

        {/* Divider */}
        <div className="w-full h-px bg-brand-border my-10 md:my-12"></div>

        {/* FAQ Section */}
        <div data-testid="faq-section">
          <div className="text-center mb-6">
            <h2 className="font-display font-black uppercase text-brand-dark tracking-tight text-2xl sm:text-3xl mb-2">
              FREQUENTLY ASKED QUESTIONS
            </h2>
            <p className="font-sans font-bold text-brand-dark/70 text-xs sm:text-sm uppercase tracking-wide">
              QUICK ANSWERS ABOUT THE DEBT COLLECTOR KILL SWITCH
            </p>
          </div>
          <FaqAccordion items={FAQ_ITEMS} />
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
